import React, { useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Button,
  Box,
  CircularProgress,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { getStatusLabel, getStatusColor, getStatusBgColor, getStatusBorderColor } from '../../utils/availability';
import { getProductUrl } from '../../config';

const AvailabilityBadge = ({ status, quantityAvailable }) => {
  const label = getStatusLabel(status, quantityAvailable);
  const color = getStatusColor(status);

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: color,
        color: 'white',
        fontWeight: 'medium',
        fontSize: '0.7rem',
        height: 22
      }}
    />
  );
};

const Home = ({ camps, weeks, locations, loading, error, fetchCamps }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Set default location on first render if locations exist
  React.useEffect(() => {
    if (locations.length > 0 && !selectedLocation) {
      setSelectedLocation(locations[0]);
    }
  }, [locations, selectedLocation]);

  if (loading) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Loading camps...</Typography>
      <CircularProgress />
    </Container>
  );

  if (error) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" color="error" sx={{ mb: 2 }}>{error}</Typography>
      <Button variant="contained" onClick={() => fetchCamps()} sx={{ mt: 2 }}>Retry</Button>
    </Container>
  );

  if (!camps.length) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>No camps available at this time</Typography>
      <Button variant="contained" onClick={() => fetchCamps()} sx={{ mt: 2 }}>Refresh</Button>
    </Container>
  );

  // Get the slot for a specific camp, week (dates), and location
  const getSlot = (camp, dates) => {
    return camp.weekSlots.find(s =>
      s.dates === dates && (selectedLocation ? s.location === selectedLocation : true)
    );
  };

  return (
    <Container maxWidth={false} sx={{ py: 4, width: '95%' }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Summer Camps 2026
      </Typography>
      <Typography variant="h5" component="h2" gutterBottom align="center" color="text.secondary" sx={{ mb: 2 }}>
        Weekly Schedule
      </Typography>

      {/* Location filter */}
      {locations.length > 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
            CHOOSE YOUR LOCATION
          </Typography>
          <ToggleButtonGroup
            value={selectedLocation}
            exclusive
            onChange={(e, val) => { if (val !== null) setSelectedLocation(val); }}
            size="small"
          >
            {locations.map(loc => (
              <ToggleButton key={loc} value={loc} sx={{ px: 3 }}>
                {loc}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Legend */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} />
          <Typography variant="caption">Available</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} />
          <Typography variant="caption">Low Availability</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336' }} />
          <Typography variant="caption">Sold Out</Typography>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4, width: '100%', overflowX: 'hidden' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: '20%' }}>Camp Type</TableCell>
              {weeks.map(week => (
                <TableCell
                  key={week}
                  align="center"
                  sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: '0.8rem', px: 1 }}
                >
                  {week}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {camps.map(camp => (
              <TableRow
                key={camp.handle}
                hover
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ fontWeight: 'bold', pl: 2, width: '20%' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {camp.imageUrl && (
                      <img
                        src={camp.imageUrl}
                        alt={camp.imageAlt}
                        style={{
                          width: '80px',
                          height: '80px',
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                    )}
                    <Box>
                      {camp.title}
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => window.open(getProductUrl(camp.handle), '_blank')}
                        sx={{
                          mt: 1,
                          display: 'block',
                          backgroundColor: '#1976d2',
                          color: 'white',
                          '&:hover': { backgroundColor: '#1565c0' }
                        }}
                      >
                        Course Info
                      </Button>
                    </Box>
                  </Box>
                </TableCell>
                {weeks.map(week => {
                  const slot = getSlot(camp, week);
                  if (!slot) return (
                    <TableCell key={`${camp.handle}-${week}`} align="center" sx={{ p: 0.5 }}>
                      —
                    </TableCell>
                  );

                  return (
                    <TableCell
                      key={`${camp.handle}-${week}`}
                      align="center"
                      sx={{ p: 0.5 }}
                    >
                      <Card
                        variant="outlined"
                        sx={{
                          backgroundColor: getStatusBgColor(slot.status),
                          border: `1px solid ${getStatusBorderColor(slot.status)}`,
                          cursor: slot.status !== 'sold_out' ? 'pointer' : 'default',
                          opacity: slot.status === 'sold_out' ? 0.7 : 1,
                          transition: 'all 0.2s',
                          '&:hover': slot.status !== 'sold_out' ? {
                            transform: 'scale(1.02)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          } : {}
                        }}
                        onClick={() => {
                          if (slot.status !== 'sold_out') {
                            window.open(getProductUrl(camp.handle), '_blank');
                          }
                        }}
                      >
                        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                          <AvailabilityBadge
                            status={slot.status}
                            quantityAvailable={slot.quantityAvailable}
                          />
                          {slot.status !== 'sold_out' && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                              ${parseFloat(slot.price).toFixed(0)}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Home;
